import React, { useState } from 'react';
import { Box, ButtonBase, CircularProgress, Paper, Typography } from '@mui/material';
import { MUTED, INK, INCIDENT_ACCENT, WARN_ORANGE, L1_ACCENT, L1_BADGE_BG, L2_ACCENT, L2_BADGE_BG, TOP_CUSTOMERS_ACCENT, TOP_CUSTOMERS_ACCENT_BG, NEUTRAL_SURFACE } from '../colorTokens';
import { ALERT_STATE_STYLES } from './monitorConstants';
import { TRIGGERED_RANGES } from './monitorStats';

// Stat-card body: small caps title, big count, a stacked proportion bar and one legend row per
// slice, driven by an ordered list of { label, value, color } slices. A slice with `header: true`
// is a group heading (label + subtotal, no dot, not part of the bar); the slices after it are indented.
const formatCount = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

export const CategoryBars = ({ title, centerValue, centerLabel, slices }) => {
  const parts = slices.filter((slice) => !slice.header);
  const grouped = slices.some((slice) => slice.header);
  const total = parts.reduce((sum, slice) => sum + slice.value, 0);
  return (
    <Box sx={{ width: 232, flexShrink: 0, textAlign: 'left' }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: MUTED }}>
        {title}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '2.25rem', lineHeight: 1.15, color: INK, mt: 1 }}>{formatCount(centerValue)}</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>{centerLabel}</Typography>
      <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: '#e2e8ea', gap: '2px', my: 1.5 }}>
        {total > 0 && parts.map((slice, i) => slice.value > 0 && (
          <Box key={`${slice.label}-${i}`} sx={{ flex: slice.value, bgcolor: slice.color, minWidth: 4 }} />
        ))}
      </Box>
      {slices.map((slice, i) => (slice.header ? (
        <Box key={`${slice.label}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: i === 0 ? 0.35 : 1, pb: 0.35 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: INK, flex: 1 }}>{slice.label}</Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: INK }}>{formatCount(slice.value)}</Typography>
        </Box>
      ) : (
        <Box key={`${slice.label}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35, pl: grouped ? 1.5 : 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: slice.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.8125rem', color: '#4a5568', flex: 1 }}>{slice.label}</Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: INK }}>{formatCount(slice.value)}</Typography>
        </Box>
      )))}
    </Box>
  );
};

/**
 * One clickable donut tile. `card` is an entry of DONUT_ROWS; `stats` comes from useMonitorStats.
 * While `card.pending(stats)` is true the tile shows `card.pendingLabel` with a spinner instead.
 */
export const DonutCard = ({ card, stats, onSelect, selected }) => {
  const pending = card.pending?.(stats);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 0, borderRadius: '10px', display: 'inline-flex', boxShadow: '0 1px 3px rgba(16, 24, 40, 0.1)',
        // Same tint as the Top Customers tile on the Vital Signs page.
        // Plus a 2px dark inset ring (so the layout doesn't shift): the tint alone is too faint to
        // be the only selected-state cue. Unselected cards have no outline, just a soft shadow.
        ...(selected && {
          bgcolor: TOP_CUSTOMERS_ACCENT_BG,
          boxShadow: `0 1px 3px rgba(16, 24, 40, 0.1), inset 0 0 0 2px ${TOP_CUSTOMERS_ACCENT}`
        }),
        ...(pending && { alignItems: 'center', justifyContent: 'center', minWidth: 290 })
      }}
    >
      {pending ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
          <CircularProgress size={16} />
          <Typography sx={{ color: MUTED, fontSize: '0.8125rem' }}>{card.pendingLabel}</Typography>
        </Box>
      ) : (
        <ButtonBase
          onClick={() => onSelect(card)}
          aria-label={card.ariaLabel}
          aria-pressed={!!selected}
          sx={{
            p: 2, borderRadius: '10px', justifyContent: 'flex-start', alignItems: 'flex-start',
            // Keyboard focus ring, drawn outside the card so it doesn't clash with the selected-state inset ring.
            '&.Mui-focusVisible': { outline: `3px solid ${TOP_CUSTOMERS_ACCENT}`, outlineOffset: 2 }
          }}
        >
          <CategoryBars
            title={card.title}
            centerValue={card.center(stats)}
            centerLabel={card.centerLabel}
            slices={card.slices(stats)}
          />
        </ButtonBase>
      )}
    </Paper>
  );
};

// Tab for each DONUT_ROWS group, in the same order as the rows. Colors follow the Vital Signs
// cards at the top of the Operational Excellence hub: accent (text / selected fill) and tint.
export const DONUT_TABS = [
  { title: 'Monitor Definitions', color: TOP_CUSTOMERS_ACCENT, bg: TOP_CUSTOMERS_ACCENT_BG },
  { title: 'Runbook/Notify Definitions', color: L1_ACCENT, bg: L1_BADGE_BG },
  { title: 'Recently Triggered', color: '#b71c1c', bg: '#fdecea' },
  { title: 'Recently Run', color: '#0d47a1', bg: L2_BADGE_BG },
  { title: 'Recently Notified', color: WARN_ORANGE, bg: '#fdf0e3' },
  // Not groups of cards: their panels are supplied by the page (DonutCardRows `trendsPanel` / `reportPanel`).
  { title: 'Trends', color: '#00695c', bg: '#e0f2f1' },
  { title: 'Generate Report', color: '#4527a0', bg: '#ede7f6' }
];

// Index of the Generate Report tab (the last one) and of the Trends tab before it.
export const REPORT_TAB_INDEX = DONUT_TABS.length - 1;
export const TRENDS_TAB_INDEX = REPORT_TAB_INDEX - 1;

// Index of the tab that starts selected (and that Show All returns to).
export const DEFAULT_DONUT_TAB = 0;

/**
 * Renders DONUT_ROWS as a row of always-visible colored tabs with the selected tab's DonutCards
 * underneath. `reportPanel` is the content of the extra Generate Report tab after them.
 */
export const DonutCardRows = ({ rows, stats, onSelect, selectedId, tabIndex, onTabChange, trendsPanel, reportPanel }) => {
  // Each extra tab is shown only when the page supplies its panel (the Generate Report tab is left out for non-admins).
  const tabs = DONUT_TABS.slice(0, reportPanel ? DONUT_TABS.length : trendsPanel ? TRENDS_TAB_INDEX + 1 : rows.length);
  const { color: activeColor } = tabs[tabIndex];

  // Tab on the last card of a group moves to the next group's tab; on the last tab it falls
  // through to the next control on the page (the Status filter).
  const onPanelKeyDown = (e, i) => {
    if (e.key !== 'Tab' || e.shiftKey || i >= tabs.length - 1) return;
    const buttons = e.currentTarget.querySelectorAll('button');
    if (e.target !== buttons[buttons.length - 1]) return;
    e.preventDefault();
    onTabChange(i + 1);
    document.getElementById(`donut-tab-${i + 1}`)?.focus();
  };

  // Left/Right arrows move between tabs.
  const onTabKeyDown = (e, i) => {
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1 }[e.key];
    if (next === undefined || next < 0 || next >= tabs.length) return;
    e.preventDefault();
    onTabChange(next);
    document.getElementById(`donut-tab-${next}`)?.focus();
  };

  return (
    <Box sx={{ width: 'max-content', maxWidth: '100%', mb: 2.5 }}>
      <Box role="tablist" aria-label="Monitor summary groups" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
        {tabs.map(({ title, color, bg }, i) => {
          const selected = i === tabIndex;
          return (
            <ButtonBase
              key={title}
              role="tab"
              id={`donut-tab-${i}`}
              aria-selected={selected}
              aria-controls={`donut-tabpanel-${i}`}
              // Only the selected tab is in the Tab order, so Tab goes from it into its cards instead of
              // on to the next tab; the last card hands focus to the next tab (see onPanelKeyDown).
              tabIndex={selected ? 0 : -1}
              onClick={() => onTabChange(i)}
              onKeyDown={(e) => onTabKeyDown(e, i)}
              sx={{
                px: 2, py: selected ? 1.5 : 0.75, borderRadius: '10px 10px 0 0',
                // Unselected tabs are muted grey with only a thin colour cue on top; the selected tab
                // is the only one with a full colour fill, and it is taller so it reads as "raised".
                border: `2px solid ${selected ? color : '#c9ced3'}`, borderTop: `4px solid ${color}`,
                borderBottomColor: selected ? color : 'transparent',
                bgcolor: selected ? color : '#f1f3f5',
                color: selected ? '#fff' : '#5f6b76',
                fontSize: '0.8125rem', fontWeight: selected ? 800 : 500,
                '&:hover': selected ? undefined : { bgcolor: bg, color },
                '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 }
              }}
            >
              {title}
            </ButtonBase>
          );
        })}
      </Box>
      {/* Every group is rendered in the same grid cell and only the selected one is visible, so the
          panel is always as tall (and wide) as the largest group and switching tabs doesn't resize it. */}
      <Box sx={{ display: 'grid', p: 1.5, border: `2px solid ${activeColor}`, borderRadius: '0 10px 10px 10px', bgcolor: NEUTRAL_SURFACE }}>
        {trendsPanel && tabIndex === TRENDS_TAB_INDEX && (
          <Box role="tabpanel" id={`donut-tabpanel-${TRENDS_TAB_INDEX}`} aria-labelledby={`donut-tab-${TRENDS_TAB_INDEX}`} sx={{ gridArea: '1 / 1', minWidth: 0 }}>
            {trendsPanel}
          </Box>
        )}
        {reportPanel && tabIndex === REPORT_TAB_INDEX && (
          <Box role="tabpanel" id={`donut-tabpanel-${REPORT_TAB_INDEX}`} aria-labelledby={`donut-tab-${REPORT_TAB_INDEX}`} sx={{ gridArea: '1 / 1' }}>
            {reportPanel}
          </Box>
        )}
        {rows.map((row, i) => (
          <Box
            key={row[0].id}
            role="tabpanel"
            id={`donut-tabpanel-${i}`}
            aria-labelledby={`donut-tab-${i}`}
            hidden={false}
            aria-hidden={i !== tabIndex}
            onKeyDown={(e) => onPanelKeyDown(e, i)}
            sx={{
              gridArea: '1 / 1', display: 'flex', gap: 1.5, flexWrap: 'nowrap', '& > *': { flexShrink: 0 },
              visibility: i === tabIndex ? 'visible' : 'hidden'
            }}
          >
            {row.map((card) => (
              <DonutCard key={card.id} card={card} stats={stats} onSelect={onSelect} selected={card.id === selectedId} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** "Group: Card" label for the selected card id (e.g. "Recently Triggered: 4 - 7 days ago"), or '' if unknown. */
export const selectedDonutLabel = (cardId) => {
  const tab = DONUT_ROWS.findIndex((row) => row.some((card) => card.id === cardId));
  if (tab < 0) return '';
  return `${DONUT_TABS[tab].title}: ${DONUT_ROWS[tab].find((card) => card.id === cardId).title}`;
};

// ---- Card config ---------------------------------------------------------------------------
// Each card: { id, title, centerLabel, ariaLabel, center(stats), slices(stats) } plus what a
// click does -- `filter` (args for applySummaryFilter), `sort` (list sort to switch to) and
// `popup` (which popup to open). Optional `pending(stats)` / `pendingLabel` for async stats.

const activePaused = (active, paused) => [
  { label: 'Active', value: active, color: ALERT_STATE_STYLES.Active.color },
  { label: 'Paused', value: paused, color: ALERT_STATE_STYLES.Paused.color }
];

// One of the 4 doc-category counts (`key` of stats.doc.*), split Active/Paused.
const docCard = ({ key, filter, ...rest }) => ({
  ...rest,
  filter,
  center: (s) => s.doc.All[key],
  slices: (s) => activePaused(s.doc.Active[key], s.doc.Paused[key])
});

// Legend text colors, each >= 4.5:1 on both white and the selected-card tint (WCAG AA). The
// donut arcs only need 3:1, so they can stay lighter than the text.
const TEXT_BLUE = '#2a6aac';
const TEXT_ORANGE = '#ac4d00';

// [stats.doc key, slice label, Active arc color, Paused arc color (>= 3:1), legend text color]
// -- order of the slices inside the All Monitors donut.
const ALL_MONITORS_SLICES = [
  ['neither', 'No Runbook; No Notification', INCIDENT_ACCENT, '#d56262', INCIDENT_ACCENT],
  ['runbookOnly', 'Runbook only', '#1b5e20', '#629065', '#1b5e20'],
  ['notifyOnly', 'Notify only', L2_ACCENT, '#5589c0', TEXT_BLUE],
  ['runbookAndNotify', 'Runbook and Notification', WARN_ORANGE, '#ce6c1c', TEXT_ORANGE]
];

export const DONUT_ROWS = [
  [
    {
      id: 'allMonitors', filter: {},
      title: 'All Monitors', centerLabel: 'total monitors', ariaLabel: 'Show all monitors',
      center: (s) => s.doc.All.total,
      // Grouped by alert state: a heading with the state's total, then its four categories.
      slices: (s) => ['Active', 'Paused'].flatMap((state) => [
        { label: state, value: s.doc[state].total, header: true },
        ...ALL_MONITORS_SLICES.map(([key, label, color, pausedColor, textColor]) => ({
          label,
          value: s.doc[state][key],
          color: state === 'Paused' ? pausedColor : color,
          textColor
        }))
      ])
    },
    {
      id: 'duplicates', filter: { isDuplicate: true }, popup: 'duplicates',
      title: 'Duplicates', centerLabel: 'duplicate monitors',
      ariaLabel: 'Show monitors that are duplicates of another monitor',
      pending: (s) => s.duplicates === null, pendingLabel: 'Finding duplicates…',
      center: (s) => s.duplicates.total,
      slices: (s) => activePaused(s.duplicates.active, s.duplicates.paused)
    },
    {
      id: 'creators', filter: {}, sort: 'creator', popup: 'creators',
      title: 'Unique Creators', centerLabel: 'unique creators',
      ariaLabel: "Show all monitors sorted by creator and list every creator's monitors",
      center: (s) => s.creatorStats.unique,
      slices: (s) => activePaused(s.creatorStats.active, s.creatorStats.paused)
    },
    {
      id: 'notifyTargets', filter: {}, sort: 'notify', popup: 'notifyGroups',
      title: 'Unique Notify Targets', centerLabel: 'unique notify targets',
      ariaLabel: 'Show all monitors sorted by notify target',
      center: (s) => s.notifyGroupStats.unique,
      slices: (s) => activePaused(s.notifyGroupStats.active, s.notifyGroupStats.paused)
    }
  ],
  [
    docCard({
      id: 'noDoc', key: 'neither', filter: { hasRunbook: false, hasNotify: false },
      title: 'No Runbook or Notify', centerLabel: 'no runbook or notify',
      ariaLabel: 'Show monitors without a runbook or notify target'
    }),
    docCard({
      id: 'notifyOnly', key: 'notifyOnly', filter: { hasRunbook: false, hasNotify: true },
      title: 'Notify Only', centerLabel: 'notify only',
      ariaLabel: 'Show monitors with a notify target but no runbook'
    }),
    docCard({
      id: 'runbookOnly', key: 'runbookOnly', filter: { hasRunbook: true, hasNotify: false },
      title: 'Runbook Only', centerLabel: 'runbook only',
      ariaLabel: 'Show monitors with a runbook but no notify target'
    }),
    docCard({
      id: 'runbookAndNotify', key: 'runbookAndNotify', filter: { hasRunbook: true, hasNotify: true },
      title: 'Runbook and Notification', centerLabel: 'runbook and notification',
      ariaLabel: 'Show monitors with both a runbook and a notify target'
    }),
    {
      id: 'runbooksDefined', filter: { hasRunbook: true }, popup: 'runbooks',
      title: 'Runbooks Defined', centerLabel: 'runbooks defined',
      ariaLabel: 'Show monitors that have a runbook, sorted by runbook',
      center: (s) => s.runbooksDefined.total,
      slices: (s) => [
        { label: 'Active only', value: s.runbooksDefined.activeOnly, color: ALERT_STATE_STYLES.Active.color },
        { label: 'Paused only', value: s.runbooksDefined.pausedOnly, color: ALERT_STATE_STYLES.Paused.color },
        { label: 'Active and Paused', value: s.runbooksDefined.both, color: WARN_ORANGE, textColor: TEXT_ORANGE }
      ]
    }
  ],
  TRIGGERED_RANGES.map((range) => ({
    id: range.id, filter: { triggeredRange: range }, sort: 'triggerInfo',
    title: range.title, centerLabel: range.centerLabel,
    ariaLabel: range.never ? 'Show monitors that have never triggered' : `Show monitors last triggered: ${range.title.toLowerCase()}`,
    center: (s) => s.triggered[range.id].total,
    slices: (s) => [
      { label: 'Runbook only', value: s.triggered[range.id].runbookOnly, color: '#1b5e20' },
      { label: 'Notify only', value: s.triggered[range.id].notifyOnly, color: L2_ACCENT, textColor: TEXT_BLUE },
      { label: 'Runbook and Notification', value: s.triggered[range.id].runbookAndNotify, color: WARN_ORANGE, textColor: TEXT_ORANGE },
      { label: 'No Runbook; No Notification', value: s.triggered[range.id].neither, color: INCIDENT_ACCENT }
    ]
  })),
  // Monitors with a runbook that last triggered in each age range (no "never triggered" -- it can't have run).
  TRIGGERED_RANGES.filter((range) => !range.never).map((range) => ({
    id: `run_${range.id}`, filter: { triggeredRange: range, hasRunbook: true }, sort: 'recentlyRun',
    title: range.title, centerLabel: 'distinct runbooks run',
    ariaLabel: `Show monitors with a runbook last triggered: ${range.title.toLowerCase()}`,
    center: (s) => s.recentlyRun[range.id].runbooks,
    slices: (s) => [
      { label: 'Runbook only', value: s.recentlyRun[range.id].runbookOnly, color: '#1b5e20' },
      { label: 'Runbook and Notification', value: s.recentlyRun[range.id].runbookAndNotify, color: WARN_ORANGE, textColor: TEXT_ORANGE }
    ]
  })),
  // Distinct notify targets of monitors that last triggered in each age range.
  TRIGGERED_RANGES.filter((range) => !range.never).map((range) => ({
    id: `notify_${range.id}`, filter: { triggeredRange: range, hasNotify: true }, sort: 'recentlyNotified', popup: 'recentlyNotified',
    title: range.title, centerLabel: 'unique notification targets',
    ariaLabel: `Show monitors with notify targets last triggered: ${range.title.toLowerCase()}`,
    center: (s) => s.recentlyNotified[range.id].total,
    slices: (s) => [
      { label: 'Unique targets only', value: s.recentlyNotified[range.id].uniqueOnly, color: L2_ACCENT, textColor: TEXT_BLUE },
      { label: 'Shared and unique', value: s.recentlyNotified[range.id].sharedAndUnique, color: WARN_ORANGE, textColor: TEXT_ORANGE },
      { label: 'Shared only', value: s.recentlyNotified[range.id].sharedOnly, color: '#1b5e20' }
    ]
  }))
];
